import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { type ColorTokens, type GetProps, getTokenValue, styled, Theme, type Token, View } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { hairline } from "../utils";
import { Interface2, Interface3 } from "./Typography";
export type BadgeVariant = "outlined" | "filled";
export type BadgeSize = "small" | "medium";
type Status = "neutral" | "positive" | "negative" | "warning";
const badgeVariants = {
  status: {
    positive: {
      backgroundColor: "$alertPositiveLight",
      borderColor: "$alertPositiveDark"
    },
    negative: {
      backgroundColor: "$alertNegativeLight",
      borderColor: "$alertNegativeDark"
    },
    warning: {
      backgroundColor: "$alertWarningLight",
      borderColor: "$alertWarningDark"
    },
    neutral: {
      backgroundColor: "$surfaceSecondary",
      borderColor: "$uiIconFill"
    }
  },
  variant: {
    outlined: {
      backgroundColor: "$transparent",
      borderWidth: hairline
    },
    filled: {
      borderWidth: 0
    }
  },
  size: {
    medium: {
      height: "$1.5",
      paddingHorizontal: "$sp1",
      maxWidth: "$11"
    },
    small: {
      height: "$1",
      paddingHorizontal: "$sp0.5",
      maxWidth: "$9"
    }
  }
} as const;
const StyledBadge = styled(View, {
  name: "Badge",
  render: "span",
  role: "status",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  gap: "$sp0.5",
  overflow: "hidden",
  borderRadius: "$brFull",
  variants: badgeVariants,
  defaultVariants: {
    status: "neutral",
    variant: "filled",
    size: "medium"
  }
});
const textColorTokens: Record<Status, ColorTokens> = {
  positive: "$alertPositiveDark",
  negative: "$alertNegativeDark",
  warning: "$alertWarningDark",
  neutral: "$textPrimary"
};
const getIconSizeToken = (size: BadgeSize): Token => `$${size === "small" ? "iconXSmall" : "iconSmall"}`;
type StyledBadgeProps = GetProps<typeof StyledBadge>;
export type BadgeProps = Omit<StyledBadgeProps, "children"> & {
  status?: Status;
  variant?: BadgeVariant;
  size?: BadgeSize;
  iconLeft?: FunctionComponent<IconProps>;
  iconRight?: FunctionComponent<IconProps>;
  children: ReactNode;
};
const truncationStyles = {
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  style: {
    overflowX: "clip",
    overflowY: "visible",
    minWidth: 0
  }
} as const;
const BadgeTextMedium = styled(Interface2, truncationStyles);
const BadgeTextSmall = styled(Interface3, truncationStyles);
export const Badge = (props: BadgeProps) => {
  const {
    status = "neutral",
    variant = "filled",
    size = "medium",
    iconLeft: IconLeft,
    iconRight: IconRight,
    children,
    ...rest
  } = props;
  const iconSize = useMemo(() => getTokenValue(getIconSizeToken(size), "size") as number, [size]);
  const foregroundColor = textColorTokens[status];
  const BadgeText = size === "small" ? BadgeTextSmall : BadgeTextMedium;
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="Badge" data-sentry-source-file="Badge.tsx">
      <StyledBadge {...rest} status={status} variant={variant} size={size} data-sentry-element="StyledBadge" data-sentry-source-file="Badge.tsx">
        {IconLeft ? <View flexShrink={0}>
            <IconLeft size={iconSize} color={foregroundColor} />
          </View> : null}
        <BadgeText color={foregroundColor} flexShrink={1} numberOfLines={1} data-sentry-element="BadgeText" data-sentry-source-file="Badge.tsx">
          {children}
        </BadgeText>
        {IconRight ? <View flexShrink={0}>
            <IconRight size={iconSize} color={foregroundColor} />
          </View> : null}
      </StyledBadge>
    </Theme>;
};